import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { PALETTE } from "./config.js";

/**
 * The underworld: a luminous grid plane marking "ground level" just
 * before the descent goes subterranean, and fossilized versions of the
 * floating models half-buried among the roots below it.
 */

const GROUND_Y = 0;
const GRID_SIZE = 110;

const MODELS = [
  // The skull sits upright, gazing level across the root bed; the rest
  // tumble at random rest angles.
  { url: "/assets/models/skull.obj", scale: 1.0, rot: [-0.5, Math.PI * 1.25, 0] },
  { url: "/assets/models/MQ-9-b.obj", scale: 1.4 },
  { url: "/assets/models/m4.obj", scale: 1.1 },
  { url: "/assets/models/teddy.obj", scale: 1.0 },
  { url: "/assets/models/gluegun.obj", scale: 1.0 },
  { url: "/assets/models/plane.obj", scale: 1.15 }
];

// Hand-placed graves: spread around the trunk, sunk to varying depths.
const FOSSIL_SPOTS = [
  { r: 4.6, az: 0.4, y: -2.6 },
  { r: 6.4, az: 1.6, y: -4.2 },
  { r: 5.2, az: 2.9, y: -3.3 },
  { r: 7.2, az: 4.1, y: -5.1 },
  { r: 4.2, az: 5.2, y: -4.6 },
  { r: 6.8, az: 6.0, y: -2.9 }
];

const GRID_VERT = /* glsl */ `
  varying vec2 vPos;
  void main() {
    vPos = position.xy; // plane is rotated flat; xy are the grid axes
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const GRID_FRAG = /* glsl */ `
  precision highp float;
  uniform float uTime;
  uniform vec3 uColor;
  varying vec2 vPos;

  void main() {
    float cell = 2.2;
    vec2 grid = abs(fract(vPos / cell - 0.5) - 0.5) * cell;
    vec2 fw = fwidth(vPos) * 1.2;
    float lx = 1.0 - smoothstep(0.0, fw.x, grid.x);
    float ly = 1.0 - smoothstep(0.0, fw.y, grid.y);
    float line = max(lx, ly);

    float r = length(vPos);
    // Fade out toward the horizon and keep a soft hole around the trunk.
    float fade = (1.0 - smoothstep(14.0, 46.0, r)) * smoothstep(1.2, 3.4, r);

    // A slow pulse ring rippling out from the trunk.
    float ring = sin(r * 0.55 - uTime * 1.1);
    ring = smoothstep(0.86, 1.0, ring) * (1.0 - smoothstep(6.0, 34.0, r));

    float a = line * fade * 0.32 + ring * fade * 0.07;
    gl_FragColor = vec4(uColor, a);
  }
`;

function buildGrid() {
  const geo = new THREE.PlaneGeometry(GRID_SIZE, GRID_SIZE, 1, 1);
  const [r, g, b] = PALETTE.stops[PALETTE.stops.length - 1].color; // amber
  const mat = new THREE.ShaderMaterial({
    vertexShader: GRID_VERT,
    fragmentShader: GRID_FRAG,
    uniforms: {
      uTime: { value: 0 },
      uColor: { value: new THREE.Color(r, g, b) }
    },
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = GROUND_Y;
  mesh.frustumCulled = false;
  return { mesh, mat };
}

/** Normalize an OBJ group to unit size, centered at its origin. */
function normalize(obj) {
  const box = new THREE.Box3().setFromObject(obj);
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const s = 1 / (Math.max(size.x, size.y, size.z) || 1);
  obj.scale.setScalar(s);
  obj.position.copy(center).multiplyScalar(-s);
  const wrapper = new THREE.Group();
  wrapper.add(obj);
  return wrapper;
}

export function buildUnderworld({ tier }) {
  const group = new THREE.Group();

  const grid = buildGrid();
  group.add(grid.mesh);

  // Dim archaeological lighting for the fossils only — everything else
  // in the scene uses unlit shader materials, so these lights are cheap
  // and touch nothing above ground.
  const hemi = new THREE.HemisphereLight(0x7d74c9, 0xcaa276, 1.5);
  group.add(hemi);
  const glow = new THREE.PointLight(0xffc98a, 55, 30, 2);
  glow.position.set(0, -4, 0);
  group.add(glow);

  // Calcified bone-stone: flat shading reads as chiseled rock and needs
  // no normals or UVs from the source OBJs.
  const fossilMat = new THREE.MeshStandardMaterial({
    color: 0xb8a98c,
    roughness: 0.96,
    metalness: 0.0,
    flatShading: true,
    emissive: 0x2a1c0c,
    emissiveIntensity: 1.0
  });

  const loader = new OBJLoader();
  const fossilCount = tier.name === "mobile" ? 4 : FOSSIL_SPOTS.length;

  FOSSIL_SPOTS.slice(0, fossilCount).forEach((spot, i) => {
    const spec = MODELS[i % MODELS.length];
    loader.load(
      spec.url,
      (obj) => {
        obj.traverse((child) => {
          if (child.isMesh) child.material = fossilMat;
        });
        const fossil = normalize(obj);
        fossil.name = "fossil-" + spec.url.split("/").pop().replace(".obj", "");
        fossil.scale.setScalar(2.4 * spec.scale);
        fossil.position.set(
          Math.cos(spot.az) * spot.r,
          spot.y,
          Math.sin(spot.az) * spot.r
        );
        if (spec.rot) {
          // YXZ: yaw first, then pitch in the model's own facing frame,
          // so rot[0] reliably tips the gaze up/down.
          fossil.rotation.order = "YXZ";
          fossil.rotation.set(spec.rot[0], spec.rot[1], spec.rot[2]);
        } else {
          // Tumbled at rest, like strata settled at odd angles.
          fossil.rotation.set(
            (Math.random() - 0.5) * 1.4,
            Math.random() * Math.PI * 2,
            (Math.random() - 0.5) * 1.4
          );
        }
        group.add(fossil);
      },
      undefined,
      () => {} // a missing model just means one fewer fossil
    );
  });

  function update(t) {
    grid.mat.uniforms.uTime.value = t;
    // Slow breathing on the buried glow.
    glow.intensity = 55 + Math.sin(t * 0.7) * 14;
  }

  return { group, update };
}
